import React from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Text,
  Select,
  Flex,
  Badge,
} from '@chakra-ui/react';
import xhr from 'xhr';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

// ============================================================
// GRADOS QUE QUIERES MOSTRAR EN EL SELECT
// ============================================================

const VALID_GRADE_IDS = [
  9, 10, 11, 12, 13, 14, 41, 42, 43, 44, 45, 67, 69, 70, 71, 72, 73,
];

// ============================================================
// ESTOS GRADOS TIENEN DOS GRUPOS
//
// Ejemplo:
// Sexto 6-1
// Sexto 6-2
//
// Cada grupo aporta máximo 5 estudiantes.
// ============================================================

const GRADES_WITH_TWO_GROUPS = [9, 10, 11, 12, 13, 14];

const PERIODS = ['primer', 'segundo', 'tercer', 'cuarto'];

const TOP_PER_GROUP = 5;

// ============================================================
// HELPERS GENERALES STRAPI
// ============================================================

function getAttrs(item) {
  return item?.attributes || item || {};
}

function unwrapRelation(relation) {
  if (!relation) return null;

  const data = relation?.data ?? relation;

  if (!data) return null;

  if (data?.attributes) {
    return {
      id: data.id,
      ...data.attributes,
    };
  }

  return data;
}

// ============================================================
// STUDENT NOTE
// ============================================================

function getStudentFromNote(studentNote) {
  const attrs = getAttrs(studentNote);

  return unwrapRelation(attrs.student);
}

function getStudentIdFromNote(studentNote) {
  const student = getStudentFromNote(studentNote);

  if (!student) return null;

  if (
    typeof student === 'number' ||
    typeof student === 'string'
  ) {
    return Number(student);
  }

  return student?.id || null;
}

function getUserFromNote(studentNote) {
  const student = getStudentFromNote(studentNote);

  if (!student) return null;

  return unwrapRelation(student.user);
}

function getGradeFromNote(studentNote) {
  const attrs = getAttrs(studentNote);

  return unwrapRelation(attrs.grade);
}

function getGradeNameFromNote(studentNote) {
  const grade = getGradeFromNote(studentNote);

  return grade?.name || '';
}

function getPeriodFromNote(studentNote) {
  const attrs = getAttrs(studentNote);

  return attrs?.period || '';
}

function getFirstNameFromNote(studentNote) {
  const user = getUserFromNote(studentNote);

  return user?.first_name || '';
}

function getLastNameFromNote(studentNote) {
  const user = getUserFromNote(studentNote);

  return user?.last_name || '';
}

function getNotesFromStudentNote(studentNote) {
  const attrs = getAttrs(studentNote);

  const notes =
    attrs?.notes?.data ??
    attrs?.notes ??
    [];

  if (!Array.isArray(notes)) {
    return [];
  }

  return notes.map((note) => ({
    id: note?.id,
    ...(note?.attributes || note),
  }));
}

// ============================================================
// GRADE ENROLLMENT
// ============================================================

function getStudentIdFromEnrollment(enrollment) {
  const attrs = getAttrs(enrollment);

  const student = unwrapRelation(
    attrs.student
  );

  if (!student) return null;

  if (
    typeof student === 'number' ||
    typeof student === 'string'
  ) {
    return Number(student);
  }

  return student?.id || null;
}

// ============================================================
// STUDENT GROUP
//
// IMPORTANTE:
// student-group
//     ↓
// class_group
//     ↓
// grade
//
// ESA ES LA RUTA QUE USAMOS.
// ============================================================

function normalizeStudentGroup(studentGroup) {
  const attrs = getAttrs(studentGroup);

  const student = unwrapRelation(
    attrs.student
  );

  const classGroup = unwrapRelation(
    attrs.class_group
  );

  // AQUÍ ESTÁ LA RELACIÓN IMPORTANTE:
  // studentGroup.class_group.grade
  const grade = unwrapRelation(
    classGroup?.grade
  );

  const studentId =
    typeof student === 'number'
      ? student
      : Number(student?.id);

  const classGroupId = Number(
    classGroup?.id
  );

  const gradeId = Number(
    grade?.id
  );

  return {
    id: studentGroup?.id,
    studentId,
    classGroupId,
    classGroupName:
      classGroup?.name || 'Sin grupo',
    gradeId,

    createdAt:
      attrs?.createdAt || null,

    updatedAt:
      attrs?.updatedAt || null,
  };
}

// ============================================================
// PROMEDIO DE UN STUDENT-NOTE
// ============================================================

function calculateAverage(studentNote) {
  const notes =
    getNotesFromStudentNote(studentNote);

  let totalScore = 0;
  let validCount = 0;

  notes.forEach((note) => {
    /*
     * NO convertir:
     *
     * null
     * undefined
     * ''
     *
     * en nota 0.
     */

    if (
      note?.score === null ||
      note?.score === undefined ||
      note?.score === ''
    ) {
      return;
    }

    const numberScore =
      Number(note.score);

    if (
      Number.isFinite(numberScore)
    ) {
      totalScore += numberScore;
      validCount += 1;
    }
  });

  if (validCount === 0) {
    return null;
  }

  return totalScore / validCount;
}

// ============================================================
// SI EXISTEN VARIOS STUDENT-NOTES DEL MISMO ESTUDIANTE
// TOMAMOS EL MÁS RECIENTE
// ============================================================

function getLatestStudentNotes(
  studentNotes
) {
  const map = new Map();

  studentNotes.forEach(
    (studentNote) => {
      const studentId =
        Number(
          getStudentIdFromNote(
            studentNote
          )
        );

      if (!studentId) return;

      const attrs =
        getAttrs(studentNote);

      const date =
        attrs?.updatedAt ||
        attrs?.createdAt ||
        null;

      const existing =
        map.get(studentId);

      if (!existing) {
        map.set(
          studentId,
          studentNote
        );

        return;
      }

      const existingAttrs =
        getAttrs(existing);

      const existingDate =
        existingAttrs?.updatedAt ||
        existingAttrs?.createdAt ||
        null;

      const currentTime = date
        ? dayjs(date).valueOf()
        : 0;

      const existingTime =
        existingDate
          ? dayjs(
            existingDate
          ).valueOf()
          : 0;

      if (
        currentTime >= existingTime
      ) {
        map.set(
          studentId,
          studentNote
        );
      }
    }
  );

  return Array.from(
    map.values()
  );
}

// ============================================================
// COMPONENTE
// ============================================================

function Promedio() {
  const [
    studentsByPeriod,
    setStudentsByPeriod,
  ] = React.useState({
    primer: [],
    segundo: [],
    tercer: [],
    cuarto: [],
  });

  const [grades, setGrades] =
    React.useState([]);

  const [
    selectedGrade,
    setSelectedGrade,
  ] = React.useState(null);

  const [loading, setLoading] =
    React.useState(false);

  const [
    loadingGrades,
    setLoadingGrades,
  ] = React.useState(true);

  // ==========================================================
  // AÑO ACTUAL
  // ==========================================================

  const currentYear =
    dayjs().year();

  const startOfYear =
    `${currentYear}-01-01T00:00:00.000Z`;

  const startOfNextYear =
    `${currentYear + 1}-01-01T00:00:00.000Z`;

  // ==========================================================
  // CARGAR GRADOS
  // ==========================================================

  React.useEffect(() => {
    const fetchGrades =
      async () => {
        try {
          setLoadingGrades(true);

          const res = await xhr(
            '/grades?pagination[limit]=-1'
          );

          const allGrades =
            res.data?.data || [];

          const validGrades =
            allGrades.filter(
              (grade) =>
                VALID_GRADE_IDS.includes(
                  Number(
                    grade.id
                  )
                )
            );

          validGrades.sort(
            (a, b) => {
              const nameA =
                getAttrs(a)?.name ||
                '';

              const nameB =
                getAttrs(b)?.name ||
                '';

              return nameA.localeCompare(
                nameB,
                'es',
                {
                  numeric: true,
                }
              );
            }
          );

          setGrades(
            validGrades
          );

          if (
            validGrades.length > 0
          ) {
            setSelectedGrade(
              Number(
                validGrades[0].id
              )
            );
          }
        } catch (error) {
          console.error(
            'Error cargando grados:',
            error
          );

          setGrades([]);
        } finally {
          setLoadingGrades(
            false
          );
        }
      };

    fetchGrades();
  }, []);

  // ==========================================================
  // 1. ESTUDIANTES QUE ESTÁN CURSANDO ACTUALMENTE EL GRADO
  //
  // grade-enrollment
  //
  // grade = seleccionado
  // status = estudiando o promocion_pendiente
  // active = true
  // ==========================================================

  const fetchActiveStudentsByGrade =
    async (gradeId) => {
      try {
        const params =
          new URLSearchParams();

        params.set(
          'filters[grade][id][$eq]',
          String(gradeId)
        );

        params.set(
          'filters[status][$in][0]',
          'estudiando'
        );

        params.set('filters[status][$in][1]', 'promocion_pendiente');

        params.set(
          'filters[active][$eq]',
          'true'
        );

        params.set(
          'populate[student]',
          'true'
        );

        params.set(
          'pagination[limit]',
          '-1'
        );

        const res = await xhr(
          `/grade-enrollments?${params.toString()}`
        );

        const enrollments =
          res.data?.data || [];

        const studentIds =
          enrollments
            .map(
              getStudentIdFromEnrollment
            )
            .filter(Boolean)
            .map(Number);

        const uniqueStudentIds = [
          ...new Set(
            studentIds
          ),
        ];

        console.log(
          `[Ranking] Grado ${gradeId} - estudiantes con matrícula activa:`,
          uniqueStudentIds
        );

        return uniqueStudentIds;
      } catch (error) {
        console.error(
          `Error cargando matrículas del grado ${gradeId}:`,
          error
        );

        return [];
      }
    };

  // ==========================================================
  // 2. BUSCAR GRUPO ACTUAL
  //
  // student-group
  //      ↓
  // class_group
  //      ↓
  // grade
  //
  // IMPORTANTE:
  // Si el estudiante tiene varios registros del MISMO grado,
  // tomamos el más reciente.
  // ==========================================================

  const fetchCurrentStudentGroups =
    async (
      gradeId,
      activeStudentIds
    ) => {
      try {
        if (
          !activeStudentIds.length
        ) {
          return new Map();
        }

        const params =
          new URLSearchParams();

        /*
         * FILTRO IMPORTANTE:
         *
         * student-group.class_group.grade
         */

        params.set(
          'filters[class_group][grade][id][$eq]',
          String(gradeId)
        );

        params.set(
          'filters[class_group][active][$eq]',
          'true'
        );

        params.set(
          'populate[student]',
          'true'
        );

        params.set(
          'populate[class_group][populate][grade]',
          'true'
        );

        params.set(
          'sort[0]',
          'createdAt:desc'
        );

        params.set(
          'pagination[limit]',
          '-1'
        );

        const res = await xhr(
          `/student-groups?${params.toString()}`
        );

        const rawStudentGroups =
          res.data?.data || [];

        console.log(
          `[Ranking] Student-groups encontrados para grado ${gradeId}:`,
          rawStudentGroups.length
        );

        const activeStudentSet =
          new Set(
            activeStudentIds.map(
              Number
            )
          );

        const normalized =
          rawStudentGroups
            .map(
              normalizeStudentGroup
            )
            .filter(
              (item) =>
                item.studentId &&
                item.classGroupId &&
                item.gradeId
            )
            .filter(
              (item) =>
                activeStudentSet.has(
                  Number(
                    item.studentId
                  )
                )
            )
            .filter(
              (item) =>
                Number(
                  item.gradeId
                ) ===
                Number(gradeId)
            );

        /*
         * Ordenamos del más reciente
         * al más antiguo.
         */

        normalized.sort(
          (a, b) => {
            const dateA =
              a.createdAt
                ? dayjs(
                  a.createdAt
                ).valueOf()
                : 0;

            const dateB =
              b.createdAt
                ? dayjs(
                  b.createdAt
                ).valueOf()
                : 0;

            return dateB - dateA;
          }
        );

        /*
         * MAP:
         *
         * studentId ->
         * grupo actual
         *
         * Como están ordenados
         * de más reciente a antiguo,
         * solamente guardamos
         * la primera aparición.
         */

        const currentGroupMap =
          new Map();

        normalized.forEach(
          (item) => {
            if (
              !currentGroupMap.has(
                Number(
                  item.studentId
                )
              )
            ) {
              currentGroupMap.set(
                Number(
                  item.studentId
                ),
                {
                  id:
                    item.classGroupId,

                  name:
                    item.classGroupName,

                  gradeId:
                    item.gradeId,

                  studentGroupId:
                    item.id,

                  createdAt:
                    item.createdAt,
                }
              );
            }
          }
        );

        console.log(
          '[Ranking] Grupo actual por estudiante:',
          Array.from(
            currentGroupMap.entries()
          )
        );

        return currentGroupMap;
      } catch (error) {
        console.error(
          `Error cargando grupos del grado ${gradeId}:`,
          error
        );

        return new Map();
      }
    };

  // ==========================================================
  // 3. STUDENT NOTES DEL AÑO ACTUAL
  // ==========================================================

  const fetchStudentNotesByGrade =
    async (gradeId) => {
      try {
        const params =
          new URLSearchParams();

        params.set(
          'filters[grade][id][$eq]',
          String(gradeId)
        );

        /*
         * Importante:
         *
         * El estudiante puede haber
         * sido creado hace años.
         *
         * SOLO filtramos el año de
         * STUDENT-NOTES.
         */

        params.set(
          'filters[createdAt][$gte]',
          startOfYear
        );

        params.set(
          'filters[createdAt][$lt]',
          startOfNextYear
        );

        params.set(
          'populate[student][populate][user]',
          'true'
        );

        params.set(
          'populate[grade]',
          'true'
        );

        params.set(
          'populate[notes]',
          'true'
        );

        params.set(
          'pagination[limit]',
          '-1'
        );

        const res = await xhr(
          `/student-notes?${params.toString()}`
        );

        const studentNotes =
          res.data?.data || [];

        console.log(
          `[Ranking] Student-notes ${currentYear} grado ${gradeId}:`,
          studentNotes.length
        );

        return studentNotes;
      } catch (error) {
        console.error(
          `Error cargando student-notes del grado ${gradeId}:`,
          error
        );

        return [];
      }
    };

  // ==========================================================
  // DETERMINAR CUÁNTOS GRUPOS DEBE USAR
  // ==========================================================

  const getExpectedGroupCount =
    (gradeId) => {
      return GRADES_WITH_TWO_GROUPS.includes(
        Number(gradeId)
      )
        ? 2
        : 1;
    };

  // ==========================================================
  // ENCONTRAR LOS GRUPOS ACTUALES RELEVANTES
  //
  // Si esperamos:
  //
  // 2 grupos -> seleccionamos los 2 grupos con más
  // estudiantes activos.
  //
  // 1 grupo -> seleccionamos el grupo con más
  // estudiantes activos.
  //
  // Esto protege contra registros antiguos/inconsistentes.
  // ==========================================================

  const getAllowedGroups = (
    studentGroupMap,
    gradeId
  ) => {
    const expectedGroupCount =
      getExpectedGroupCount(
        gradeId
      );

    const groupCounts =
      new Map();

    studentGroupMap.forEach(
      (group) => {
        const groupId =
          Number(group.id);

        if (
          !groupCounts.has(
            groupId
          )
        ) {
          groupCounts.set(
            groupId,
            {
              id: groupId,
              name:
                group.name ||
                'Sin grupo',
              count: 0,
            }
          );
        }

        groupCounts.get(
          groupId
        ).count += 1;
      }
    );

    /*
     * Primero ordenamos por
     * cantidad de estudiantes.
     *
     * Así, si por alguna razón
     * quedaron grupos antiguos,
     * elegimos los realmente usados.
     */

    const groups =
      Array.from(
        groupCounts.values()
      )
        .sort(
          (a, b) =>
            b.count - a.count
        )
        .slice(
          0,
          expectedGroupCount
        );

    /*
     * Para mostrar:
     *
     * 6-1
     * 6-2
     *
     * ordenados.
     */

    groups.sort((a, b) =>
      String(
        a.name
      ).localeCompare(
        String(b.name),
        'es',
        {
          numeric: true,
        }
      )
    );

    if (
      groups.length <
      expectedGroupCount
    ) {
      console.warn(
        `[Ranking] El grado ${gradeId} esperaba ${expectedGroupCount} grupo(s), pero solo se encontraron ${groups.length}.`
      );
    }

    return groups;
  };

  // ==========================================================
  // GENERAR RANKING DE UN PERIODO
  // ==========================================================

  const buildPeriodRanking = (
    studentNotes,
    studentGroupMap,
    gradeId
  ) => {
    /*
     * Evitar registros duplicados
     * de student-notes.
     */

    const uniqueStudentNotes =
      getLatestStudentNotes(
        studentNotes
      );

    /*
     * Determinamos cuáles grupos
     * vamos a utilizar.
     */

    const allowedGroups =
      getAllowedGroups(
        studentGroupMap,
        gradeId
      );

    const allowedGroupIds =
      new Set(
        allowedGroups.map(
          (group) =>
            Number(group.id)
        )
      );

    /*
     * Calcular promedio
     * y asociar grupo.
     */

    const candidates =
      uniqueStudentNotes
        .map(
          (studentNote) => {
            const studentId =
              Number(
                getStudentIdFromNote(
                  studentNote
                )
              );

            if (
              !studentId
            ) {
              return null;
            }

            const group =
              studentGroupMap.get(
                studentId
              );

            /*
             * El estudiante debe
             * tener grupo actual
             * correspondiente al grado.
             */

            if (!group) {
              return null;
            }

            if (
              !allowedGroupIds.has(
                Number(
                  group.id
                )
              )
            ) {
              return null;
            }

            const averageScore =
              calculateAverage(
                studentNote
              );

            if (
              averageScore ===
              null
            ) {
              return null;
            }

            return {
              student:
                studentNote,

              studentId,

              averageScore,

              groupId:
                Number(
                  group.id
                ),

              groupName:
                group.name,

              groupRank: 0,
            };
          }
        )
        .filter(Boolean);

    // ========================================================
    // AGRUPAR CANDIDATOS POR CLASS_GROUP
    // ========================================================

    const candidatesByGroup =
      new Map();

    allowedGroups.forEach(
      (group) => {
        candidatesByGroup.set(
          Number(group.id),
          []
        );
      }
    );

    candidates.forEach(
      (candidate) => {
        if (
          candidatesByGroup.has(
            candidate.groupId
          )
        ) {
          candidatesByGroup
            .get(
              candidate.groupId
            )
            .push(
              candidate
            );
        }
      }
    );

    // ========================================================
    // TOP 5 DE CADA GRUPO
    // ========================================================

    const ranking = [];

    allowedGroups.forEach(
      (group) => {
        const groupStudents =
          candidatesByGroup.get(
            Number(group.id)
          ) || [];

        const topStudents =
          groupStudents
            .sort(
              (a, b) =>
                b.averageScore -
                a.averageScore
            )
            .slice(
              0,
              TOP_PER_GROUP
            )
            .map(
              (
                student,
                index
              ) => ({
                ...student,

                groupRank:
                  index + 1,
              })
            );

        ranking.push(
          ...topStudents
        );
      }
    );

    return ranking;
  };

  // ==========================================================
  // CARGAR RANKING
  // ==========================================================

  React.useEffect(() => {
    if (
      selectedGrade === null
    ) {
      return;
    }

    let cancelled = false;

    const loadRanking =
      async () => {
        setLoading(true);

        try {
          // ==================================================
          // 1. QUIÉNES ESTÁN ESTUDIANDO ACTUALMENTE
          // ==================================================

          const activeStudentIds =
            await fetchActiveStudentsByGrade(
              selectedGrade
            );

          if (cancelled) return;

          if (
            activeStudentIds.length ===
            0
          ) {
            setStudentsByPeriod({
              primer: [],
              segundo: [],
              tercer: [],
              cuarto: [],
            });

            return;
          }

          // ==================================================
          // 2. BUSCAR GRUPOS Y NOTAS
          // ==================================================

          const [
            studentGroupMap,
            allStudentNotes,
          ] = await Promise.all([
            fetchCurrentStudentGroups(
              selectedGrade,
              activeStudentIds
            ),

            fetchStudentNotesByGrade(
              selectedGrade
            ),
          ]);

          if (cancelled) return;

          const activeStudentSet =
            new Set(
              activeStudentIds.map(
                Number
              )
            );

          // ==================================================
          // 3. FILTRAR NOTAS SOLO DE ESTUDIANTES CON
          // MATRÍCULA ACTUAL
          // ==================================================

          const validStudentNotes =
            allStudentNotes.filter(
              (
                studentNote
              ) => {
                const studentId =
                  Number(
                    getStudentIdFromNote(
                      studentNote
                    )
                  );

                if (
                  !studentId
                ) {
                  return false;
                }

                /*
                 * Debe estar
                 * estudiando actualmente.
                 */

                if (
                  !activeStudentSet.has(
                    studentId
                  )
                ) {
                  return false;
                }

                /*
                 * También debe
                 * tener grupo actual.
                 */

                if (
                  !studentGroupMap.has(
                    studentId
                  )
                ) {
                  return false;
                }

                return true;
              }
            );

          console.log(
            '[Ranking] Notas válidas después de matrícula + grupo:',
            validStudentNotes.length
          );

          // ==================================================
          // 4. DIVIDIR POR PERIODO
          // ==================================================

          const notesByPeriod = {
            primer: [],
            segundo: [],
            tercer: [],
            cuarto: [],
          };

          validStudentNotes.forEach(
            (
              studentNote
            ) => {
              const period =
                getPeriodFromNote(
                  studentNote
                );

              if (
                PERIODS.includes(
                  period
                )
              ) {
                notesByPeriod[
                  period
                ].push(
                  studentNote
                );
              }
            }
          );

          // ==================================================
          // 5. TOP 5 POR GRUPO
          // ==================================================

          const bestPrimer =
            buildPeriodRanking(
              notesByPeriod.primer,
              studentGroupMap,
              selectedGrade
            );

          const bestSegundo =
            buildPeriodRanking(
              notesByPeriod.segundo,
              studentGroupMap,
              selectedGrade
            );

          const bestTercer =
            buildPeriodRanking(
              notesByPeriod.tercer,
              studentGroupMap,
              selectedGrade
            );

          const bestCuarto =
            buildPeriodRanking(
              notesByPeriod.cuarto,
              studentGroupMap,
              selectedGrade
            );

          console.log(
            '[Ranking] Primer periodo:',
            bestPrimer
          );

          console.log(
            '[Ranking] Segundo periodo:',
            bestSegundo
          );

          console.log(
            '[Ranking] Tercer periodo:',
            bestTercer
          );

          console.log(
            '[Ranking] Cuarto periodo:',
            bestCuarto
          );

          setStudentsByPeriod({
            primer:
              bestPrimer,

            segundo:
              bestSegundo,

            tercer:
              bestTercer,

            cuarto:
              bestCuarto,
          });
        } catch (error) {
          console.error(
            'Error cargando ranking:',
            error
          );

          if (!cancelled) {
            setStudentsByPeriod({
              primer: [],
              segundo: [],
              tercer: [],
              cuarto: [],
            });
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

    loadRanking();

    return () => {
      cancelled = true;
    };
  }, [selectedGrade]);

  // ==========================================================
  // NOMBRE DEL GRADO SELECCIONADO
  // ==========================================================

  const selectedGradeName =
    React.useMemo(() => {
      const grade =
        grades.find(
          (item) =>
            Number(item.id) ===
            Number(
              selectedGrade
            )
        );

      return (
        getAttrs(grade)
          ?.name || ''
      );
    }, [
      grades,
      selectedGrade,
    ]);

  // ==========================================================
  // RENDER TABLA
  // ==========================================================

  const renderTable = (
    title,
    data
  ) => {
    const expectedGroups =
      getExpectedGroupCount(
        selectedGrade
      );

    return (
      <Box mt={10}>
        <Flex
          justify="space-between"
          align="center"
          flexWrap="wrap"
          gap={3}
          mb={3}
        >
          <Box>
            <Heading size="md">
              Mejores estudiantes
              del {title} periodo
            </Heading>

            <Text
              fontSize="sm"
              color="gray.500"
              mt={1}
            >
              {selectedGradeName}{' '}
              - {currentYear}
            </Text>
          </Box>

          <Badge
            colorScheme="purple"
            px={3}
            py={1}
            borderRadius="full"
          >
            {expectedGroups === 2
              ? 'Top 5 por cada grupo'
              : 'Top 5 del grupo'}
          </Badge>
        </Flex>

        {data.length === 0 ? (
          <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            p={6}
            bg="gray.50"
          >
            <Text color="gray.500">
              No hay datos
              disponibles para este
              periodo.
            </Text>
          </Box>
        ) : (
          <TableContainer
            mt="10px"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
          >
            <Table
              variant="simple"
              size="sm"
            >
              <Thead bg="gray.100">
                <Tr>
                  <Th>
                    Puesto
                  </Th>

                  <Th>
                    ID
                  </Th>

                  <Th>
                    Nombre
                  </Th>

                  <Th>
                    Apellido
                  </Th>

                  <Th>
                    Grado
                  </Th>

                  <Th>
                    Grupo
                  </Th>

                  <Th
                    isNumeric
                  >
                    Promedio
                  </Th>

                  <Th>
                    Ver
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {data.map(
                  (
                    item,
                    index
                  ) => {
                    const studentNote =
                      item.student;

                    const studentId =
                      getStudentIdFromNote(
                        studentNote
                      );

                    return (
                      <Tr
                        key={`${title}-${item.groupId}-${studentId || index}`}
                      >
                        <Td>
                          <Badge
                            colorScheme={
                              item.groupRank ===
                                1
                                ? 'yellow'
                                : 'gray'
                            }
                            borderRadius="full"
                            px={2}
                          >
                            {
                              item.groupRank
                            }
                          </Badge>
                        </Td>

                        <Td>
                          {
                            studentId
                          }
                        </Td>

                        <Td>
                          {getFirstNameFromNote(
                            studentNote
                          )}
                        </Td>

                        <Td>
                          {getLastNameFromNote(
                            studentNote
                          )}
                        </Td>

                        <Td>
                          {getGradeNameFromNote(
                            studentNote
                          )}
                        </Td>

                        <Td>
                          <Badge
                            colorScheme="purple"
                            variant="subtle"
                          >
                            {
                              item.groupName
                            }
                          </Badge>
                        </Td>

                        <Td
                          isNumeric
                          fontWeight="600"
                        >
                          {Number.isFinite(
                            item.averageScore
                          )
                            ? item.averageScore.toFixed(
                              2
                            )
                            : '0.00'}
                        </Td>

                        <Td>
                          {studentId ? (
                            <Link
                              to={`/plataforma/administrador/ver-estudiante/${studentId}`}
                              style={{
                                color:
                                  '#574feb',
                                fontWeight:
                                  500,
                              }}
                            >
                              Ver
                            </Link>
                          ) : (
                            '-'
                          )}
                        </Td>
                      </Tr>
                    );
                  }
                )}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  // ==========================================================
  // RENDER PRINCIPAL
  // ==========================================================

  return (
    <Box p={4}>
      <Heading mb={2}>
        Ranking de Estudiantes
        por Periodo
      </Heading>

      <Text
        color="gray.600"
        mb={5}
        fontSize="sm"
      >
        El ranking toma únicamente
        estudiantes que actualmente
        se encuentran estudiando el
        grado seleccionado y valida
        su grupo académico actual.
      </Text>

      {/* SELECT GRADO */}

      {loadingGrades ? (
        <Flex
          align="center"
          gap={3}
          mb={5}
        >
          <Spinner size="sm" />

          <Text>
            Cargando grados...
          </Text>
        </Flex>
      ) : (
        <Select
          placeholder="Selecciona un grado"
          mb={4}
          maxW="500px"
          value={
            selectedGrade || ''
          }
          onChange={(e) =>
            setSelectedGrade(
              Number(
                e.target.value
              )
            )
          }
        >
          {grades.map(
            (grade) => (
              <option
                key={grade.id}
                value={grade.id}
              >
                {
                  getAttrs(grade)
                    ?.name
                }
              </option>
            )
          )}
        </Select>
      )}

      {/* LOADING */}

      {loading ? (
        <Flex
          direction="column"
          justify="center"
          align="center"
          py={20}
          gap={4}
        >
          <Spinner
            size="xl"
            color="#574feb"
          />

          <Text
            color="gray.500"
            fontSize="sm"
          >
            Calculando ranking...
          </Text>
        </Flex>
      ) : (
        <>
          {renderTable(
            'primer',
            studentsByPeriod.primer
          )}

          {renderTable(
            'segundo',
            studentsByPeriod.segundo
          )}

          {renderTable(
            'tercer',
            studentsByPeriod.tercer
          )}

          {renderTable(
            'cuarto',
            studentsByPeriod.cuarto
          )}
        </>
      )}
    </Box>
  );
}

export default Promedio;